import type { Scene } from '@babylonjs/core/scene';

import { FACE } from './atlas';
import type { ModelPalette } from './palette';
import { box, capsule, createRig, cylinder, type Rig, sphere, torus, wedge } from './rig';

/** Hip height. Slightly under the crusader — a husk is a hunched thing. */
const HIP_Y = 0.8;

/** A skeleton's limbs: a little longer in the shin than the crusader's, and much thinner. */
const LIMBS = { upperArm: 0.22, foreArm: 0.24, thigh: 0.3, shin: 0.33 };

/**
 * The Skeleton Warrior — the mob.
 *
 * Deliberately the crusader's opposite in every readable way: bone against
 * steel, gaps against solid mass, a hunched forward lean against an upright
 * stance. At camera distance you should be able to tell friend from foe by
 * silhouette alone, before the nameplate resolves.
 *
 * **Phase 41: the ribs are rings.** The old ribcage was four flat boxes stacked with air
 * between them, which from above — the only angle this camera has — read as a ladder. A
 * rib is a hoop round the spine, and four flattened tori with air between them read as a
 * ribcage from every angle, for the same part count. The skull is a sphere with a jaw and
 * a brow; the limbs are the thinnest capsules in the game, split at the elbow and the knee
 * so the shamble has joints in it.
 */
export function buildSkeleton(scene: Scene, palette: ModelPalette): Rig {
  const rig = createRig(scene, 'skeleton', HIP_Y, LIMBS, 0.3, 0.14, 0.44);
  const parts = rig.parts;

  const bone = palette.get('bone');
  const boneDark = palette.get('boneDark');
  const rust = palette.get('rust');
  const ember = palette.get('ember');

  // ── Spine and pelvis ──────────────────────────────────────────────────────
  sphere(scene, rig.torso, parts, {
    name: 'skl-pelvis',
    size: [0.34, 0.16, 0.22],
    at: [0, 0.02, 0],
    material: boneDark,
    segments: 4,
  });
  cylinder(scene, rig.torso, parts, {
    name: 'skl-spine',
    height: 0.56,
    diameterTop: 0.08,
    diameterBottom: 0.11,
    at: [0, 0.32, -0.03],
    material: boneDark,
    tessellation: 6,
  });

  // ── Ribcage: four hoops with gaps, tapering upward ────────────────────────
  const ribs: [number, number][] = [
    [0.2, 0.44],
    [0.31, 0.42],
    [0.42, 0.36],
    [0.52, 0.28],
  ];
  for (const [i, [y, width]] of ribs.entries()) {
    torus(scene, rig.torso, parts, {
      name: `skl-rib${i}`,
      diameter: width,
      thickness: 0.05,
      at: [0, y, 0.0],
      // A ribcage is deeper than it is wide at the bottom and rounder at the top; the
      // hoops are squashed front-to-back to say so.
      rotation: [0, 0, 0],
      material: bone,
      tessellation: 10,
    }).scaling.z = 0.62;
  }
  // Collarbone — the widest bone, so the shoulders read.
  capsule(scene, rig.torso, parts, {
    name: 'skl-clavicle',
    height: 0.52,
    radiusTop: 0.035,
    radiusBottom: 0.035,
    at: [0, 0.6, 0.02],
    rotation: [0, 0, Math.PI / 2],
    material: bone,
    tessellation: 6,
  });

  // ── Skull ─────────────────────────────────────────────────────────────────
  // The cranium is the one part of a monster this change touches: the ashen-skull cell from
  // the face sheet, in place of flat bone.
  //
  // **The cell is on the head's material rather than on a face plate of its own**, which is
  // the opposite of the crusader's arrangement and is deliberate. A sphere's UVs wrap the
  // whole picture once around it, so the skull appears on the sides as well as the front —
  // paid knowingly, because the alternatives are worse here. A plate would be a mesh per
  // monster and would z-fight the burning sockets below, which sit forward of the skull and
  // are the thing that says the monster is awake. And a skull wrapping a skull-shaped sphere
  // is the one subject where the repeat is nearly right: the camera is fixed isometric, so a
  // player sees this head's front and one side, and a cranium reads as a cranium from both.
  sphere(scene, rig.head, parts, {
    name: 'skl-cranium',
    size: [0.26, 0.26, 0.28],
    at: [0, 0.11, 0.0],
    material: palette.cell(FACE.skullAshen, 'bone'),
    segments: 6,
  });
  box(scene, rig.head, parts, {
    name: 'skl-brow',
    size: [0.26, 0.05, 0.05],
    at: [0, 0.13, 0.13],
    material: boneDark,
  });
  // Two burning sockets — the only light on the model, and what tells you at a
  // glance that it is looking at you.
  for (const [side, x] of [
    ['R', -0.06],
    ['L', 0.06],
  ] as const) {
    sphere(scene, rig.head, parts, {
      name: `skl-eye${side}`,
      size: [0.06, 0.05, 0.04],
      at: [x, 0.08, 0.13],
      material: ember,
      segments: 3,
    });
  }
  sphere(scene, rig.head, parts, {
    name: 'skl-jaw',
    size: [0.2, 0.09, 0.22],
    at: [0, -0.02, 0.03],
    material: boneDark,
    segments: 4,
  });

  // ── Arms: bare bone, thinner than the crusader's plate, jointed at the elbow ──
  for (const [side, arm, fore, hand] of [
    ['R', rig.armR, rig.foreR, rig.handR],
    ['L', rig.armL, rig.foreL, rig.handL],
  ] as const) {
    capsule(scene, arm, parts, {
      name: `skl-arm${side}`,
      height: LIMBS.upperArm + 0.04,
      radiusTop: 0.04,
      radiusBottom: 0.035,
      at: [0, -LIMBS.upperArm / 2, 0],
      material: bone,
      tessellation: 6,
    });
    capsule(scene, fore, parts, {
      name: `skl-fore${side}`,
      height: LIMBS.foreArm + 0.02,
      radiusTop: 0.035,
      radiusBottom: 0.04,
      at: [0, -LIMBS.foreArm / 2, 0],
      material: bone,
      tessellation: 6,
    });
    sphere(scene, hand, parts, {
      name: `skl-hand${side}`,
      size: [0.1, 0.1, 0.1],
      at: [0, -0.01, 0],
      material: boneDark,
      segments: 3,
    });
  }

  // ── Weapon: a notched, rusted cleaver ─────────────────────────────────────
  //
  // Into rig.heldParts, not rig.parts: a weapon is not the body, so the collider is
  // not measured from it. See rig.ts.
  cylinder(scene, rig.weapon, rig.heldParts, {
    name: 'skl-grip',
    height: 0.16,
    diameterTop: 0.05,
    diameterBottom: 0.06,
    at: [0, -0.06, 0],
    material: boneDark,
    tessellation: 6,
  });
  wedge(scene, rig.weapon, rig.heldParts, {
    name: 'skl-cleaver',
    size: [0.16, 0.62, 0.04],
    taper: 0.7,
    flip: true,
    at: [0, -0.44, 0.02],
    material: rust,
  });
  // A notch out of the blade, made by overlapping a smaller dark box.
  box(scene, rig.weapon, rig.heldParts, {
    name: 'skl-notch',
    size: [0.09, 0.1, 0.06],
    at: [0.06, -0.32, 0.02],
    material: boneDark,
  });

  // ── Legs, jointed at the knee ─────────────────────────────────────────────
  for (const [side, leg, shin] of [
    ['R', rig.legR, rig.shinR],
    ['L', rig.legL, rig.shinL],
  ] as const) {
    capsule(scene, leg, parts, {
      name: `skl-leg${side}`,
      height: LIMBS.thigh + 0.04,
      radiusTop: 0.055,
      radiusBottom: 0.045,
      at: [0, -LIMBS.thigh / 2, 0],
      material: bone,
      tessellation: 6,
    });
    capsule(scene, shin, parts, {
      name: `skl-shin${side}`,
      height: LIMBS.shin,
      radiusTop: 0.045,
      radiusBottom: 0.035,
      at: [0, -LIMBS.shin / 2 + 0.02, 0],
      material: bone,
      tessellation: 6,
    });
    box(scene, shin, parts, {
      name: `skl-foot${side}`,
      size: [0.11, 0.06, 0.22],
      at: [0, -LIMBS.shin + 0.02, 0.05],
      material: boneDark,
    });
  }

  return rig;
}
